import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Activity, ArrowRight, Lock, X, GitBranch, Zap, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

// Animated routing simulation shown after AI qualification
export default function RouteSimulation({ data, sessionId, onClose }) {
  const [phase, setPhase] = useState(0); // 0: extract, 1: route, 2: bids, 3: winner
  const [claimMode, setClaimMode] = useState(false);
  useEffect(() => {
    const timers = [setTimeout(() => setPhase(1), 700), setTimeout(() => setPhase(2), 1700), setTimeout(() => setPhase(3), 2900)];
    return () => timers.forEach(clearTimeout);
  }, []);

  if (!data) return null;
  const winningBuyer = data.routing?.winning_buyer || "Matched buyer";
  const fixedCpl = data.estimated_value || 0;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-up" data-testid="route-simulation">
      <div className="relative w-full max-w-4xl rounded-3xl bg-white shadow-float overflow-hidden border border-slate-200 max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 z-10" data-testid="route-close" aria-label="Close">
          <X className="size-4" />
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-slate-100">
          <div className="flex items-center gap-2 text-primary">
            <Sparkles className="size-4" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase">Live routing simulation</span>
          </div>
          <h2 className="mt-3 text-3xl tracking-[-0.02em] font-semibold text-slate-900">Your enquiry, routed in real time.</h2>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">This is exactly how Connexis would qualify, price and deliver this enquiry to one exclusive buyer in production — sub-second, fixed price per sector.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-0">
          {/* Left: extracted intent */}
          <div className="p-8 border-r border-slate-100">
            <div className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-4">Qualified intent</div>
            <Row k="Sector" v={data.sector_label} />
            <Row k="Location" v={data.location || "—"} />
            <Row k="Affordability" v={(data.affordability_band || "—").toString().toUpperCase()} />
            <Row k="Confidence" v={<span className="inline-flex items-center gap-2"><span className="font-mono">{data.confidence}%</span><span className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${data.confidence}%` }} /></span></span>} />
            <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Captured intent</div>
              <p className="mt-1.5 text-sm font-medium text-slate-900 leading-relaxed">"{data.intent}"</p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 text-xs text-emerald-700">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> {data.qualified ? "Qualified for delivery" : "Partially qualified"}
            </div>
          </div>

          {/* Right: fixed-price routing flow */}
          <div className="p-8 bg-slate-50/40">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-500">Routing engine</div>
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Activity className="size-3" /> {data.routing?.latency_ms}ms</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-3 py-1.5 text-xs">
              <GitBranch className="size-3.5" /> {data.routing?.ping_tree}
            </div>

            <svg viewBox="0 0 360 160" className="w-full mt-4">
              <defs>
                <linearGradient id="rlineSrc" x1="0" x2="1">
                  <stop offset="0%"   stopColor="#0055FF" stopOpacity="0" />
                  <stop offset="60%"  stopColor="#0055FF" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#0055FF" stopOpacity="0.9" />
                </linearGradient>
                <linearGradient id="rlineOut" x1="0" x2="1">
                  <stop offset="0%"   stopColor="#22D3A6" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#22D3A6" stopOpacity="0.4" />
                </linearGradient>
                <style>{`
                  @keyframes rlinePulse { 0% { stroke-dashoffset: 60; } 100% { stroke-dashoffset: 0; } }
                  .rsim-line-src { stroke-dasharray: 60; animation: rlinePulse 1.4s ease-in-out infinite; }
                  .rsim-line-out { stroke-dasharray: 80; animation: rlinePulse 1.6s ease-in-out infinite 0.5s; }
                `}</style>
              </defs>

              {/* SRC node */}
              <g style={{ opacity: phase >= 1 ? 1 : 0, transition: "opacity 300ms ease" }}>
                <rect x="6" y="68" width="88" height="28" rx="6" fill="#0F172A" />
                <text x="50" y="86" textAnchor="middle" fontSize="11" fill="#fff" fontFamily="Instrument Sans">Enquiry</text>
              </g>

              {/* Source → Engine line */}
              <path
                d="M 94 82 C 130 82, 150 80, 168 80"
                stroke="url(#rlineSrc)"
                strokeWidth="1.8"
                fill="none"
                strokeLinecap="round"
                className={phase >= 1 ? "rsim-line-src" : ""}
                style={{ opacity: phase >= 1 ? 1 : 0, transition: "opacity 300ms ease" }}
              />

              {/* Connexis routing engine */}
              <g style={{ opacity: phase >= 2 ? 1 : 0.4, transition: "opacity 400ms ease" }}>
                <rect x="168" y="60" width="56" height="44" rx="10" fill="#0F172A" stroke="#3d7bff" strokeWidth={phase >= 2 ? "1.4" : "0.8"} />
                <g transform="translate(196,82)">
                  <path d="M -8 -5 h 6 M -8 0 h 12 M -8 5 h 6" strokeLinecap="round" stroke="#ffffff" strokeWidth="1.4" fill="none" />
                  <circle cx="6"  cy="-5" r="1.8" fill="#3d7bff" />
                  <circle cx="6"  cy="5"  r="1.8" fill="#3d7bff" />
                </g>
                <text x="196" y="120" textAnchor="middle" fontSize="8.5" letterSpacing="1.2" fill="#94A3B8" fontFamily="Instrument Sans">CONNEXIS</text>
              </g>

              {/* Engine → Buyer line */}
              <path
                d="M 224 82 C 260 82, 280 80, 296 80"
                stroke="url(#rlineOut)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                className={phase >= 3 ? "rsim-line-out" : ""}
                style={{ opacity: phase >= 3 ? 1 : 0, transition: "opacity 400ms ease" }}
              />

              {/* Matched buyer card */}
              <g style={{ opacity: phase >= 3 ? 1 : 0.45, transition: "opacity 500ms ease" }}>
                <rect
                  x="296" y="62"
                  width="60" height="40"
                  rx="8"
                  fill={phase >= 3 ? "rgba(34,211,166,0.10)" : "#fff"}
                  stroke={phase >= 3 ? "#22D3A6" : "#E2E8F0"}
                  strokeWidth={phase >= 3 ? "1.4" : "1"}
                />
                <text x="326" y="78" textAnchor="middle" fontSize="8" letterSpacing="1.3" fill={phase >= 3 ? "#059669" : "#94A3B8"} fontFamily="JetBrains Mono" fontWeight="600">EXCL.</text>
                <text x="326" y="92" textAnchor="middle" fontSize="10" fill={phase >= 3 ? "#059669" : "#94A3B8"} fontFamily="JetBrains Mono" fontWeight="700">£{fixedCpl.toFixed(0)}</text>
              </g>
            </svg>

            <div className={`mt-3 rounded-xl border bg-white p-4 transition-all duration-500 ${phase >= 3 ? "border-emerald-400/60 shadow-soft" : "border-slate-200 opacity-50"}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="inline-grid place-items-center size-8 rounded-md bg-emerald-500/10 text-emerald-600 flex-shrink-0"><Lock className="size-4" strokeWidth={2.2} /></span>
                  <div className="leading-tight min-w-0">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500">Routed to · exclusive buyer</div>
                    <div className="text-sm font-medium text-slate-900 truncate">{winningBuyer}</div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Fixed CPL</div>
                  <div className="text-xl font-semibold tabular-nums text-emerald-600">£{fixedCpl.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CLAIM SECTION */}
        <ClaimPanel
          sessionId={sessionId}
          data={data}
          claimMode={claimMode}
          setClaimMode={setClaimMode}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

function ClaimPanel({ sessionId, data, claimMode, setClaimMode, onClose }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  if (user) {
    // already signed in — just send them to dashboard
    const replayUrl = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/replay/${sessionId}`;
    return (
      <div className="px-8 py-5 bg-slate-50/40 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => copyToClipboard(replayUrl, "Replay link copied")} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="route-share-replay">
          <Zap className="size-3.5 text-primary" /> Share this replay · <span className="font-mono text-slate-700">/replay/{sessionId.slice(0, 8)}…</span>
        </button>
        <Button onClick={() => nav("/dashboard")} className="rounded-full px-5" data-testid="route-cta-dashboard">Open your dashboard <ArrowRight className="size-4" /></Button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="px-8 py-7 bg-emerald-50/40 border-t border-emerald-100 text-center">
        <CheckCircle2 className="size-8 text-emerald-600 mx-auto" />
        <h3 className="mt-3 text-xl font-semibold text-slate-900">Your account is live.</h3>
        <p className="mt-1 text-sm text-slate-600">Redirecting you to your dashboard — your enquiry should appear in the LIVE feed any second.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post(`/ai/chat/${sessionId}/claim`, form);
      localStorage.setItem(TOKEN_KEY, res.token);
      setSuccess(true);
      toast.success("Account created. Your enquiry is now live in your dashboard.");
      setTimeout(() => { window.location.href = "/dashboard?welcome=1"; }, 1400);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not claim — try a different email.");
    } finally {
      setBusy(false);
    }
  };

  if (!claimMode) {
    const replayUrl = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/replay/${sessionId}`;
    return (
      <div className="px-8 py-6 bg-gradient-to-r from-slate-900 to-slate-800 text-white border-t border-slate-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="max-w-md">
            <div className="flex items-center gap-2 text-primary">
              <Sparkles className="size-4" />
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/80">Make it real</span>
            </div>
            <h3 className="mt-2 text-xl font-medium tracking-tight">Claim this enquiry into your own dashboard.</h3>
            <p className="mt-1.5 text-sm text-slate-300">We'll create a Connexis client account in seconds and drop this AI-qualified lead straight into your live feed.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => copyToClipboard(replayUrl, "Replay link copied")} className="rounded-full h-11 px-4 text-sm border border-white/20 hover:bg-white/10 transition-colors inline-flex items-center gap-1.5" data-testid="route-share-replay">
              <Zap className="size-3.5" /> Share replay
            </button>
            <Button onClick={() => setClaimMode(true)} className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="route-claim-start">
              Claim this lead <ArrowRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="px-8 py-6 bg-slate-50/40 border-t border-slate-100" data-testid="claim-form">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-medium tracking-tight text-slate-900">Create your client account</h3>
          <p className="text-sm text-slate-600">This enquiry will land in your dashboard immediately. No credit card.</p>
        </div>
        <button type="button" onClick={() => setClaimMode(false)} className="text-xs text-slate-500 hover:text-slate-900">Cancel</button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Full name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="claim-name" /></div>
        <div><Label className="text-xs">Work email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="claim-email" /></div>
        <div><Label className="text-xs">Password</Label><Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="claim-password" /></div>
      </div>
      <Button disabled={busy} type="submit" className="mt-4 rounded-full px-5 h-11" data-testid="claim-submit">
        {busy ? <><Loader2 className="size-4 animate-spin" /> Creating…</> : <>Create account + claim lead <ArrowRight className="size-4" /></>}
      </Button>
    </form>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-widest text-slate-500">{k}</span>
      <span className="text-sm font-medium text-slate-900">{v}</span>
    </div>
  );
}
